// TODO(TECH-13548): move UGC onto Embla with the carousel consolidation. The theme's card carousel already runs on Embla;
// this is the old hand-rolled native-scroll carousel, kept only because UGCVideoCarousel drives native scrolling itself.
class UGCCardCarousel extends HTMLElement {
	constructor() {
		super();
		this.left = 0;
		this._boundMouseMove = this.handleMouseMove.bind(this);
		this._boundMouseUp = this.handleMouseUp.bind(this);
		this.elements = {
			arrows: this.querySelector('.carousel__arrows'),
			button_next: this.querySelector('[data-carousel-next]'),
			button_prev: this.querySelector('[data-carousel-prev]'),
			view: this.querySelector('.carousel__view'),
		};
		this.addElementListeners();
		this.setDimensions();
		this.handleNavVisibility();
	}
	addElementListeners() {
		if (this.elements.button_next) {
			this.elements.button_next.addEventListener('click', this.next.bind(this));
		}
		if (this.elements.button_prev) {
			this.elements.button_prev.addEventListener('click', this.prev.bind(this));
		}
		this.elements.view.addEventListener('scroll', this.handleScroll.bind(this));
		this.elements.view.addEventListener('pointerdown', this.handleMouseDown.bind(this));
		this.elements.view.addEventListener('dragstart', (e) => e.preventDefault());
		window.addEventListener('resize', this.handleResize.bind(this));
	}
	addViewStyleOverrides() {
		const view = this.elements.view;
		view.style.pointerEvents = 'none';
		view.style.scrollBehavior = 'unset';
		view.style.scrollSnapStop = 'unset';
		view.style.scrollSnapType = 'none';
		view.style.userSelect = 'none';
	}
	handleMouseDown(event) {
		if (event.pointerType !== 'mouse' || event.button !== 0) {
			return;
		}
		this.mouseDown = true;
		this.mouseStart = event.clientX;
		this.left = this.elements.view.scrollLeft;
		document.addEventListener('pointermove', this._boundMouseMove);
		document.addEventListener('pointerup', this._boundMouseUp);
	}
	handleMouseMove(event) {
		if (!this.mouseDown) {
			return;
		}
		const diff = this.mouseStart - event.clientX;
		const diffLimit = 10;
		if (Math.abs(diff) > diffLimit) {
			this.addViewStyleOverrides();
		}
		this.elements.view.scrollLeft = this.left + diff;
	}
	handleMouseUp() {
		this.mouseDown = false;
		this.left = this.elements.view.scrollLeft;
		this.removeViewStyleOverrides();
		document.removeEventListener('pointermove', this._boundMouseMove);
		document.removeEventListener('pointerup', this._boundMouseUp);
	}
	handleNavVisibility() {
		const arrows = this.elements.arrows;
		if (!arrows) {
			return;
		}
		const view = this.elements.view;
		arrows.classList.toggle('carousel__arrows--hidden', view.scrollWidth <= view.offsetWidth);
	}
	handleResize() {
		this.setDimensions();
		this.scrollView();
		this.handleNavVisibility();
	}
	handleScroll() {
		if (!this.ticking) {
			window.requestAnimationFrame(() => {
				const view = this.elements.view;
				if (this.elements.button_prev) {
					this.elements.button_prev.disabled = view.scrollLeft <= 1;
				}
				if (this.elements.button_next) {
					this.elements.button_next.disabled = view.scrollWidth <= view.scrollLeft + view.clientWidth + 1;
				}
				this.ticking = false;
			});
			this.ticking = true;
		}
	}
	next() {
		const view = this.elements.view;
		view.scrollTo({ left: view.scrollLeft + this.card_width + this.gap, behavior: 'smooth' });
	}
	prev() {
		const view = this.elements.view;
		view.scrollTo({ left: view.scrollLeft - (this.card_width + this.gap), behavior: 'smooth' });
	}
	removeViewStyleOverrides() {
		const view = this.elements.view;
		view.style.removeProperty('pointer-events');
		view.style.removeProperty('scroll-behavior');
		view.style.removeProperty('scroll-snap-stop');
		view.style.removeProperty('scroll-snap-type');
		view.style.removeProperty('user-select');
	}
	scrollView() {
		this.elements.view.scrollTo({ left: this.left });
	}
	setDimensions() {
		const view = this.elements.view;
		const card = Array.from(view.children).find((child) => child.offsetWidth > 0);
		this.card_width = card?.offsetWidth ?? 0;
		this.gap = parseInt(getComputedStyle(view).columnGap) || 0;
		this.left = view.scrollLeft;
	}
}

if (!customElements.get('ugc-card-carousel')) {
	customElements.define('ugc-card-carousel', UGCCardCarousel);
}

class UGCVideoCarousel extends HTMLElement {
	connectedCallback() {
		this.view = this.querySelector('.carousel__view');
		this.cards = Array.from(this.querySelectorAll('[data-ugc-card]'));
		if (!this.view || !this.cards.length) {
			return;
		}
		this.autoplay = this.hasAttribute('data-autoplay');
		this.inViewport = false;
		// Set while the visitor plays a card with sound; suppresses muted autoplay/hover.
		this.engagedCard = null;
		this.hoverCard = null;
		// Set when the visitor pauses a card themselves; suppresses autoplay until they play again.
		this.userPausedCard = null;
		// "Selected" = the is-center card. Follows scroll proximity, or the last click.
		this.selectedCard = null;
		this._scrollRaf = null;

		this.addEventListener('click', this.handleClick.bind(this));
		this.cards.forEach((card) => {
			card.addEventListener('pointerenter', (event) => {
				if (event.pointerType === 'mouse') {
					this.handleHover(card);
				}
			});
			card.addEventListener('pointerleave', (event) => {
				if (event.pointerType === 'mouse') {
					this.handleHoverEnd(card);
				}
			});
			const video = card.querySelector('video');
			if (video) {
				video.addEventListener('play', () => {
					card.classList.add('is-playing');
					this.syncControls(card);
				});
				video.addEventListener('pause', () => {
					card.classList.remove('is-playing');
					this.syncControls(card);
				});
				video.addEventListener('volumechange', () => {
					card.classList.toggle('is-unmuted', !video.muted);
					this.syncControls(card);
				});
			}
		});
		this.boundScroll = this.handleScroll.bind(this);
		this.view.addEventListener('scroll', this.boundScroll, { passive: true });
		window.addEventListener('resize', this.boundScroll);

		this.observer = new IntersectionObserver(this.handleIntersect.bind(this), { threshold: 0.35 });
		this.observer.observe(this);
		this.selectCard(this.centerCard());
	}

	disconnectedCallback() {
		this.view?.removeEventListener('scroll', this.boundScroll);
		window.removeEventListener('resize', this.boundScroll);
		this.observer?.disconnect();
	}

	handleIntersect(entries) {
		this.inViewport = entries[0].isIntersecting;
		if (this.inViewport) {
			this.playSelectedMuted();
		} else {
			this.pauseAll();
			this.engagedCard = null;
			// Scrolling doesn't fire pointerleave, so a hover left over from clicking a
			// control goes stale and would block playSelectedMuted on re-entry.
			this.hoverCard = null;
		}
	}

	handleScroll() {
		if (this._scrollRaf) {
			return;
		}
		this._scrollRaf = requestAnimationFrame(() => {
			this._scrollRaf = null;
			this.selectCard(this.centerCard());
			if (this.engagedCard && !this.isCardVisible(this.engagedCard)) {
				this.pauseCard(this.engagedCard);
				this.engagedCard = null;
			}
			this.playSelectedMuted();
		});
	}

	handleHover(card) {
		// Never steal playback from a video the visitor is watching with sound, and never resume
		// one they deliberately paused — playSelectedMuted honours the same state.
		if (this.engagedCard || card === this.userPausedCard) {
			return;
		}
		this.hoverCard = card;
		for (const other of this.cards) {
			if (other !== card) {
				this.pauseCard(other);
			}
		}
		const video = card.querySelector('video');
		if (video && video.paused) {
			video.preload = 'auto';
			video.muted = true;
			video.play().catch(() => {});
		}
	}

	handleHoverEnd(card) {
		if (this.hoverCard !== card) {
			return;
		}
		this.hoverCard = null;
		if (this.engagedCard) {
			return;
		}
		// The selected card keeps playing; previews on other cards stop.
		if (card !== this.selectedCard) {
			this.pauseCard(card);
		}
		this.playSelectedMuted();
	}

	handleClick(event) {
		const card = event.target.closest('[data-ugc-card]');
		const video = card?.querySelector('video');
		if (!video) {
			return;
		}

		if (event.target.closest('[data-ugc-playpause]')) {
			if (video.paused) {
				video.preload = 'auto';
				video.play().catch(() => {});
				this.userPausedCard = null;
				if (!video.muted) {
					this.engagedCard = card;
				}
			} else {
				video.pause();
				this.engagedCard = null;
				this.userPausedCard = card;
			}
			return;
		}

		if (event.target.closest('[data-ugc-mute]')) {
			video.muted = !video.muted;
			this.engagedCard = video.muted ? null : card;
			return;
		}

		if (event.target.closest('[data-ugc-toggle]')) {
			// Big overlay on a non-selected card: make it the active one and play with sound.
			this.selectCard(card, true);
			this.pauseOthers(card);
			video.preload = 'auto';
			video.muted = false;
			if (video.paused) {
				video.play().catch(() => {});
			}
			this.engagedCard = card;
			this.userPausedCard = null;
		}
	}

	selectCard(card, scrollTo = false) {
		if (!card) {
			return;
		}
		this.selectedCard = card;
		for (const other of this.cards) {
			other.classList.toggle('is-center', other === card);
		}
		if (scrollTo) {
			// Horizontal-only centering; scrollIntoView could also scroll the page vertically.
			const delta = card.offsetLeft + card.offsetWidth / 2 - (this.view.scrollLeft + this.view.clientWidth / 2);
			this.view.scrollBy({ left: delta, behavior: 'smooth' });
		}
	}

	centerCard() {
		const viewRect = this.view.getBoundingClientRect();
		const viewCenter = viewRect.left + viewRect.width / 2;
		let nearest = null;
		let nearestDistance = Infinity;
		for (const card of this.cards) {
			const rect = card.getBoundingClientRect();
			const distance = Math.abs(rect.left + rect.width / 2 - viewCenter);
			if (distance < nearestDistance) {
				nearestDistance = distance;
				nearest = card;
			}
		}
		return nearest;
	}

	playSelectedMuted() {
		if (!this.autoplay || !this.inViewport || this.engagedCard || this.hoverCard) {
			return;
		}
		const card = this.selectedCard;
		if (!card || card === this.userPausedCard) {
			return;
		}
		this.pauseOthers(card);
		const video = card.querySelector('video');
		if (video && video.paused) {
			video.preload = 'auto';
			video.muted = true;
			video.play().catch(() => {});
		}
	}

	syncControls(card) {
		const video = card.querySelector('video');
		const playpause = card.querySelector('[data-ugc-playpause]');
		const mute = card.querySelector('[data-ugc-mute]');
		if (playpause) {
			playpause.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
		}
		if (mute) {
			mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
		}
	}

	isCardVisible(card) {
		const viewRect = this.view.getBoundingClientRect();
		const rect = card.getBoundingClientRect();
		return rect.right > viewRect.left && rect.left < viewRect.right;
	}

	pauseCard(card) {
		const video = card.querySelector('video');
		if (video && !video.paused) {
			video.pause();
		}
		if (video) {
			video.muted = true;
		}
	}

	pauseOthers(card) {
		for (const other of this.cards) {
			if (other !== card) {
				this.pauseCard(other);
			}
		}
	}

	pauseAll() {
		this.cards.forEach((card) => this.pauseCard(card));
	}
}

if (!customElements.get('ugc-video-carousel')) {
	customElements.define('ugc-video-carousel', UGCVideoCarousel);
}
